/**
 * @fileoverview Authentication initializer component.
 * @summary Checks if user is authenticated on app startup and loads user data.
 *
 * @package KaraoNow
 * @module apps/front/src/components/AuthInitializer
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '../store/index.js'
import { getCurrentUser } from '../store/slices/authSlice.js'

/**
 * @summary AuthInitializer component.
 * @summary Checks authentication status on mount and loads user if authenticated.
 * @returns {null} This component doesn't render anything.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function AuthInitializer() {
  const dispatch = useDispatch<AppDispatch>()
  const { hasCheckedAuth } = useSelector((state: RootState) => state.auth)

  useEffect(() => {
    // Only check authentication once on app startup
    // hasCheckedAuth is stored in Redux state, so it persists across re-renders
    // This prevents multiple API calls and ensures session persistence after page refresh
    if (!hasCheckedAuth) {
      // Attempt to get current user from cookies (httpOnly JWT)
      // The backend will return 401 if no valid session exists, which is handled gracefully
      // by the reducer (sets isLoading: false and isAuthenticated: false, hasCheckedAuth: true)
      dispatch(getCurrentUser()).catch(() => {
        // Silently handle errors - user is simply not authenticated
        // The reducer already handles the error state and sets hasCheckedAuth: true
      })
    }
  }, [dispatch, hasCheckedAuth])

  // This component doesn't render anything
  return null
}

